import clsx from "clsx";
import React from "react";
import { Button, Popover, Box, Typography, Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ChevronDownIcon as ArrowDropDown } from "lucide-react";

import {
  BodypartType,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { ExerciseFiltersContent } from "./ExerciseFiltersContent";
import ResetFiltersButton from "./ResetFiltersButton";
import { getAllFilters, getFilterStatus } from "./filterUtils";

const useStyles = makeStyles((theme) => ({
  root: { fontWeight: "bold", fontSize: 15 },
  active: { color: theme.palette.primary.main },
  inactive: { color: theme.palette.text.primary },
  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(0),
  },
  header: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
}));

type ExerciseCategoriesFilterProps<
  T extends BodypartType | MovementPatternType,
> = {
  className?: string;
  filters: Record<T, boolean>;
  onChange: (filters: Record<T, boolean>) => void;
  filterTypesLabels: Record<T, string>;
  labelType: string;
};

export function ExerciseCategoriesFilter<
  T extends BodypartType | MovementPatternType,
>({
  className,
  filters,
  onChange,
  filterTypesLabels,
  labelType,
  ...other
}: ExerciseCategoriesFilterProps<T>) {
  const s = useStyles();
  const ref = React.useRef<HTMLButtonElement>();
  const [open, setOpen] = React.useState(false);

  const isAllSelected = Object.values(filters).every((v) => v);

  const filterStatus = React.useMemo(() => getFilterStatus(filters), [filters]);

  return (
    <>
      <Button
        ref={ref}
        className={clsx(
          s.root,
          filterStatus.activeCount ? s.active : s.inactive,
          className,
        )}
        onClick={() => setOpen(true)}
        {...other}
      >
        {labelType} {filterStatus.text}
        <ArrowDropDown size={20} style={{ marginLeft: 2 }} />
      </Button>

      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        classes={{ paper: s.paper }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 99999999, overflowY: "hidden" }}
      >
        <Box className={s.header}>
          <Typography
            className={s.headerText}
          >{`Filter by ${labelType}`}</Typography>
          <ResetFiltersButton
            disabled={isAllSelected}
            tooltipPlacement="bottom"
            onClick={() => onChange(getAllFilters(filters, true))}
          />
        </Box>
        <Divider />
        <Box sx={{ overflowY: "auto", maxHeight: 450 }}>
          <ExerciseFiltersContent
            filters={filters}
            onChange={onChange}
            filterTypesLabels={filterTypesLabels}
          />
        </Box>
      </Popover>
    </>
  );
}
